import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wrench, Plus, Trash, PencilSimple, X, User, FilePdf, ClockCounterClockwise, Camera, Warning, CheckCircle, ImageSquare, QrCode } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useSearch } from '../components/VerticalTabs';
import { fmtDateFR, fmtDateTimeFR } from '../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'outillage', 'electricite', 'plomberie', 'menuiserie', 'maconnerie',
  'peinture', 'echafaudage', 'securite', 'mesure', 'transport', 'autre'
];

const CONDITIONS = [
  { value: 'neuf', label: 'Neuf' },
  { value: 'bon', label: 'Bon état' },
  { value: 'usage', label: 'Usagé' },
  { value: 'reparation', label: 'En réparation' },
  { value: 'hors_service', label: 'Hors service' }
];

const EquipmentPage = ({ embedded }) => {
  const [equipment, setEquipment] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState('');
  const [showReports, setShowReports] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportsFilter, setReportsFilter] = useState('open');
  const [form, setForm] = useState({
    name: '', category: 'outillage', brand: '', purchase_date: '',
    purchase_price: 0, condition: 'bon', amortization_years: '', assigned_to: '', notes: ''
  });
  const [pendingPhotos, setPendingPhotos] = useState([]); // {file, preview}
  const photoInputRef = useRef(null);

  const handlePendingFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const accepted = [];
    for (const f of files) {
      if (f.size > 15 * 1024 * 1024) { toast.error(`Trop lourd : ${f.name}`); continue; }
      accepted.push({ file: f, preview: URL.createObjectURL(f) });
    }
    setPendingPhotos(prev => [...prev, ...accepted]);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };
  const removePendingPhoto = (idx) => {
    setPendingPhotos(prev => {
      const t = prev[idx];
      if (t?.preview) URL.revokeObjectURL(t.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };
  const clearPendingPhotos = () => {
    pendingPhotos.forEach(p => { if (p.preview) URL.revokeObjectURL(p.preview); });
    setPendingPhotos([]);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const uploadBatchToExisting = async (files) => {
    if (!editItem?.id || !files.length) return;
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    try {
      await axios.post(`${API_URL}/api/equipment/${editItem.id}/photos/batch`, fd, { withCredentials: true });
      toast.success(`${files.length} photo(s) ajoutee(s)`);
      const res = await axios.get(`${API_URL}/api/equipment`, { withCredentials: true });
      setEquipment(res.data);
      const updated = (res.data || []).find(e => e.id === editItem.id);
      if (updated) setEditItem(updated);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur upload'); }
  };

  const handleDeletePhotoExtra = async (photoId) => {
    if (!editItem?.id) return;
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await axios.delete(`${API_URL}/api/equipment/${editItem.id}/photos/extra/${photoId}`, { withCredentials: true });
      toast.success('Photo supprimee');
      const res = await axios.get(`${API_URL}/api/equipment`, { withCredentials: true });
      setEquipment(res.data);
      const updated = (res.data || []).find(e => e.id === editItem.id);
      if (updated) setEditItem(updated);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const fetchData = useCallback(async () => {
    try {
      const [eqRes, empRes] = await Promise.all([
        axios.get(`${API_URL}/api/equipment`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users/employees`, { withCredentials: true })
      ]);
      setEquipment(eqRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        purchase_price: parseFloat(form.purchase_price) || 0,
        amortization_years: form.amortization_years ? parseFloat(form.amortization_years) : null,
        assigned_to: form.assigned_to === 'none' ? null : form.assigned_to || null
      };
      let savedItem = editItem;
      if (editItem) {
        await axios.put(`${API_URL}/api/equipment/${editItem.id}`, payload, { withCredentials: true });
        toast.success('Matériel mis à jour');
      } else {
        const res = await axios.post(`${API_URL}/api/equipment`, payload, { withCredentials: true });
        toast.success('Matériel ajouté');
        savedItem = { id: res.data?.id, ...payload, photos: [] };
      }
      // Upload pending photos (if any) attached during creation/edit
      if (pendingPhotos.length > 0 && savedItem?.id) {
        const fd = new FormData();
        pendingPhotos.forEach(p => fd.append('files', p.file));
        try {
          await axios.post(`${API_URL}/api/equipment/${savedItem.id}/photos/batch`, fd, { withCredentials: true });
          toast.success(`${pendingPhotos.length} photo(s) ajoutee(s)`);
        } catch (e2) {
          toast.error(e2.response?.data?.detail || 'Erreur upload photos');
        }
        clearPendingPhotos();
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce matériel ?')) return;
    try {
      await axios.delete(`${API_URL}/api/equipment/${id}`, { withCredentials: true });
      toast.success('Matériel supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      name: item.name,
      category: item.category,
      brand: item.brand || '',
      purchase_date: item.purchase_date || '',
      purchase_price: item.purchase_price || 0,
      condition: item.condition || 'bon',
      amortization_years: item.amortization_years || '',
      assigned_to: item.assigned_to || '',
      notes: item.notes || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditItem(null);
    setForm({ name: '', category: 'outillage', brand: '', purchase_date: '', purchase_price: 0, condition: 'bon', amortization_years: '', assigned_to: '', notes: '' });
    clearPendingPhotos();
  };

  const conditionBadge = (cond) => {
    const colors = { neuf: '#2E7D32', bon: '#1565C0', usage: '#EF6C00', reparation: '#C62828', hors_service: '#424242' };
    const label = CONDITIONS.find(c => c.value === cond)?.label || cond;
    return <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: `${colors[cond] || '#666'}15`, color: colors[cond] || '#666' }}>{label}</span>;
  };

  const handleAssign = async (equipmentId, userId) => {
    try {
      const item = equipment.find(e => e.id === equipmentId);
      if (!item) return;
      await axios.put(`${API_URL}/api/equipment/${equipmentId}`, {
        name: item.name, category: item.category, brand: item.brand || '',
        purchase_date: item.purchase_date || '', purchase_price: item.purchase_price || 0,
        condition: item.condition || 'bon',
        amortization_years: item.amortization_years || null,
        assigned_to: userId, notes: item.notes || ''
      }, { withCredentials: true });
      toast.success(userId ? 'Materiel affecte' : 'Materiel desaffecte');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const openHistory = async () => {
    setShowHistory(true);
    try {
      const res = await axios.get(`${API_URL}/api/equipment/history`, { withCredentials: true });
      setHistory(res.data || []);
    } catch { toast.error('Erreur chargement historique'); }
  };

  const fetchReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/equipment/reports`, { withCredentials: true });
      setReports(res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const openReports = () => { fetchReports(); setShowReports(true); };

  const resolveReport = async (id) => {
    try {
      await axios.put(`${API_URL}/api/equipment/reports/${id}/resolve`, {}, { withCredentials: true });
      toast.success('Signalement marque comme resolu');
      fetchReports();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openCount = reports.filter(r => r.status === 'ouvert').length;

  const downloadEquipmentQr = async (eqId, mini = false) => {
    try {
      const res = await axios.get(`${API_URL}/api/equipment/${eqId}/qr-code${mini ? '?mini=true' : ''}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur generation QR'); }
  };

  const downloadQrBatch = async (mini = false) => {
    try {
      const ids = filtered.map(e => e.id);
      const res = await axios.post(`${API_URL}/api/equipment/qr-batch${mini ? '?mini=true' : ''}`, { ids }, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success(`Planche QR ${mini ? 'mini (22mm)' : 'standard'} generee (${ids.length} materiel(s))`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur generation planche QR'); }
  };

  const handleUploadPhoto = async (eqId, slot, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/equipment/${eqId}/photos/${slot}`, fd, { withCredentials: true });
      toast.success(`Photo ${slot} ajoutee`);
      const res = await axios.get(`${API_URL}/api/equipment`, { withCredentials: true });
      setEquipment(res.data);
      const updated = (res.data || []).find(e => e.id === eqId);
      if (updated) setEditItem(updated);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur upload'); }
  };

  const handleDeletePhoto = async (eqId, slot) => {
    if (!window.confirm(`Supprimer la photo ${slot} ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/equipment/${eqId}/photos/${slot}`, { withCredentials: true });
      toast.success('Photo supprimee');
      const res = await axios.get(`${API_URL}/api/equipment`, { withCredentials: true });
      setEquipment(res.data);
      const updated = (res.data || []).find(e => e.id === eqId);
      if (updated) setEditItem(updated);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const { searchTerm } = useSearch();

  const filtered = (filterCategory === 'all' ? equipment : equipment.filter(e => e.category === filterCategory)).filter(e => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (e.name || '').toLowerCase().includes(s) || (e.brand || '').toLowerCase().includes(s) || (e.category || '').toLowerCase().includes(s) || (e.assigned_name || '').toLowerCase().includes(s);
  });
  const totalValue = filtered.reduce((s, e) => s + (e.purchase_price || 0), 0);

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="equipment-page">
      {!embedded && (
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Materiel</h1>
          <p className="page-subtitle">{equipment.length} materiel(s) - Valeur totale: {fmtDateTimeFR(totalValue)}EUR</p>
        </div>
        <button data-testid="add-equipment-btn" className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          <Plus size={18} weight="bold" /> Ajouter
        </button>
      </div>
      )}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <p style={{ color: '#5E5F66', fontSize: '0.9rem', margin: 0, alignSelf: 'center' }}>{equipment.length} materiel(s) - Valeur: {fmtDateTimeFR(totalValue)}EUR</p>
          <button data-testid="add-equipment-btn" className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <Plus size={18} weight="bold" /> Ajouter
          </button>
        </div>
      )}

      <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger style={{ width: '220px' }}>
            <SelectValue placeholder="Filtrer par catégorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les catégories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={openReports} data-testid="equipment-reports-btn" style={{ position: 'relative', color: openCount > 0 ? '#C62828' : undefined, borderColor: openCount > 0 ? '#FFCDD2' : undefined }}>
            <Warning size={16} /> Signalements
            {openCount > 0 && (
              <span style={{ marginLeft: '6px', backgroundColor: '#C62828', color: 'white', borderRadius: '10px', padding: '1px 7px', fontSize: '0.7rem', fontWeight: '700' }}>{openCount}</span>
            )}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={openHistory} data-testid="equipment-history-btn">
            <ClockCounterClockwise size={16} /> Historique
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => downloadQrBatch(false)} data-testid="equipment-qr-batch-btn">
            <QrCode size={16} /> Planche QR
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => downloadQrBatch(true)} data-testid="equipment-qr-batch-mini-btn">
            <QrCode size={16} /> Planche mini (22mm)
          </button>
          <span style={{ fontSize: '0.85rem', color: '#5E5F66', marginLeft: '6px' }}>Emargement materiel :</span>
          <Select value="" onValueChange={(uid) => {
            if (!uid) return;
            window.open(`${API_URL}/api/pdf-reports/employees/${uid}/equipment`, '_blank');
          }}>
            <SelectTrigger style={{ width: '260px' }} data-testid="select-employee-equipment-pdf">
              <SelectValue placeholder="Choisir un salarie..." />
            </SelectTrigger>
            <SelectContent className="z-[9999]">
              {employees.filter(e => equipment.some(eq => eq.assigned_to === e.id)).map(e => {
                const count = equipment.filter(eq => eq.assigned_to === e.id).length;
                return <SelectItem key={e.id} value={e.id}>{e.name} ({count})</SelectItem>;
              })}
              {employees.filter(e => equipment.some(eq => eq.assigned_to === e.id)).length === 0 && (
                <SelectItem value="none" disabled>Aucun salarie n'a de materiel affecte</SelectItem>
              )}
            </SelectContent>
          </Select>
          <FilePdf size={16} style={{ color: '#C62828' }} />
        </div>
      </div>

      <div className="card">
        {filtered.length > 0 ? (
          <table className="data-table" data-testid="equipment-table">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>Photo</th>
                <th>Nom</th>
                <th>Catégorie</th>
                <th>Marque</th>
                <th>Date achat</th>
                <th>Prix</th>
                <th>État</th>
                <th>Amortissement</th>
                <th>Affecté à</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const primaryPhoto = item.photos?.[0];
                return (
                <tr key={item.id} onClick={() => openEdit(item)} style={{ cursor: 'pointer' }} data-testid={`equipment-row-${item.id}`}>
                  <td>
                    {primaryPhoto ? (
                      <div style={{ position: 'relative', width: 48, height: 48 }}>
                        <img
                          src={`${API_URL}${primaryPhoto.url}?v=${item.id}`}
                          alt={item.name}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #E2E2E6' }}
                        />
                        {item.photos.length > 1 && (
                          <span style={{ position: 'absolute', bottom: -4, right: -4, background: '#002FA7', color: 'white', borderRadius: 999, padding: '1px 6px', fontSize: '0.62rem', fontWeight: 800, border: '2px solid white' }}>{item.photos.length}</span>
                        )}
                      </div>
                    ) : (
                      <div style={{ width: '48px', height: '48px', borderRadius: '8px', background: 'linear-gradient(135deg, #EEF1FC, #E2E8F7)', border: '1px solid #E2E2E6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9E9E9E' }}><ImageSquare size={20} /></div>
                    )}
                  </td>
                  <td style={{ fontWeight: '600' }}>{item.name}</td>
                  <td>{item.category}</td>
                  <td>{item.brand || '-'}</td>
                  <td>{item.purchase_date || '-'}</td>
                  <td>{item.purchase_price ? `${fmtDateTimeFR(item.purchase_price)}€` : '-'}</td>
                  <td>{conditionBadge(item.condition)}</td>
                  <td>{item.amortization_years ? `${item.amortization_years} ans` : '-'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <Select value={item.assigned_to || 'none'} onValueChange={(v) => handleAssign(item.id, v === 'none' ? null : v)}>
                      <SelectTrigger
                        style={{ height: '30px', fontSize: '0.8rem', minWidth: '140px', backgroundColor: item.assigned_to ? '#E3F2FD' : 'transparent', color: item.assigned_to ? '#1565C0' : '#5E5F66', fontWeight: item.assigned_to ? '600' : '400' }}
                        data-testid={`assign-equipment-${item.id}`}
                      >
                        <SelectValue placeholder="Non affecte" />
                      </SelectTrigger>
                      <SelectContent className="z-[9999]">
                        <SelectItem value="none">Non affecte</SelectItem>
                        {employees.map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button className="btn btn-secondary btn-sm" title="QR code" onClick={() => downloadEquipmentQr(item.id)} data-testid={`qr-equipment-${item.id}`}><QrCode size={14} /></button>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(item)} data-testid={`edit-equipment-${item.id}`}><PencilSimple size={14} /></button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.id)}><Trash size={14} /></button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <Wrench size={48} className="empty-state-icon" />
            <p className="empty-state-text">Aucun matériel enregistré</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '4px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>{editItem ? 'Modifier le matériel' : 'Ajouter un matériel'}</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
              <div className="form-group">
                <label className="form-label">Nom *</label>
                <input data-testid="eq-name" type="text" className="form-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Catégorie</label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <label className="form-label">Marque</label>
                  <input type="text" className="form-input" value={form.brand} onChange={(e) => setForm({...form, brand: e.target.value})} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Date d'achat</label>
                  <input type="date" className="form-input" value={form.purchase_date} onChange={(e) => setForm({...form, purchase_date: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Prix d'achat (€)</label>
                  <input type="number" step="0.01" className="form-input" value={form.purchase_price} onChange={(e) => setForm({...form, purchase_price: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Amortissement (années)</label>
                  <input type="number" step="0.5" className="form-input" value={form.amortization_years} onChange={(e) => setForm({...form, amortization_years: e.target.value})} placeholder="Ex: 5" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">État</label>
                  <Select value={form.condition} onValueChange={(v) => setForm({...form, condition: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONDITIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <label className="form-label">Affecté à</label>
                  <Select value={form.assigned_to || 'none'} onValueChange={(v) => setForm({...form, assigned_to: v === 'none' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Non affecté" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non affecté</SelectItem>
                      {employees.map(emp => <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-input" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} />
              </div>

              {/* Photos section - works for both new and existing equipment */}
              <div className="form-group" data-testid="eq-photos-section">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Camera size={14} /> Photos du materiel
                  {editItem && <span style={{ fontWeight: 400, color: '#5E5F66', fontSize: '0.78rem' }}>({(editItem.photos || []).length} en ligne)</span>}
                </label>
                {/* Existing photos (if editing) */}
                {editItem && (editItem.photos || []).length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8, marginBottom: 10 }}>
                    {editItem.photos.map((p) => (
                      <div key={p.id} data-testid={`eq-photo-${p.id}`} style={{ position: 'relative', borderRadius: 6, overflow: 'hidden', border: '1px solid #E2E2E6', background: '#F4F5F7' }}>
                        <img
                          src={`${API_URL}${p.url}`}
                          alt={p.filename}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }}
                        />
                        {p.slot ? (
                          <button
                            type="button"
                            onClick={() => handleDeletePhoto(editItem.id, p.slot)}
                            data-testid={`eq-photo-delete-slot-${p.slot}`}
                            style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(198, 40, 40, 0.85)', border: 'none', color: 'white', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          ><Trash size={11} /></button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleDeletePhotoExtra(p.id)}
                            data-testid={`eq-photo-delete-${p.id}`}
                            style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(198, 40, 40, 0.85)', border: 'none', color: 'white', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          ><Trash size={11} /></button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* Pending photos previews (before save) */}
                {pendingPhotos.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8, marginBottom: 10, padding: 8, background: '#EFF6FF', borderRadius: 6, border: '1px dashed #BFDBFE' }}>
                    {pendingPhotos.map((p, idx) => (
                      <div key={idx} style={{ position: 'relative', borderRadius: 6, overflow: 'hidden', border: '1px solid #E2E2E6' }}>
                        <img src={p.preview} alt={`pending ${idx + 1}`} style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
                        <button
                          type="button"
                          onClick={() => removePendingPhoto(idx)}
                          data-testid={`eq-pending-photo-remove-${idx}`}
                          style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        ><X size={11} weight="bold" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Upload buttons */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    ref={photoInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      if (editItem) {
                        // Direct upload to existing equipment
                        await uploadBatchToExisting(files);
                        if (photoInputRef.current) photoInputRef.current.value = '';
                      } else {
                        // Defer until save
                        handlePendingFiles(e);
                      }
                    }}
                    data-testid="eq-photos-input"
                  />
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    className="btn btn-secondary btn-sm"
                    data-testid="eq-photos-add-btn"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <Camera size={14} /> {editItem ? 'Ajouter des photos' : 'Selectionner des photos'}
                  </button>
                  {!editItem && pendingPhotos.length > 0 && (
                    <>
                      <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{pendingPhotos.length} photo(s) en attente</span>
                      <button type="button" onClick={clearPendingPhotos} className="btn btn-secondary btn-sm">Tout retirer</button>
                    </>
                  )}
                  {!editItem && pendingPhotos.length === 0 && (
                    <span style={{ fontSize: '0.78rem', color: '#5E5F66', fontStyle: 'italic' }}>Astuce : prenez les photos directement avec votre telephone</span>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} style={{ flex: 1 }}>Annuler</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} data-testid="eq-submit">{editItem ? 'Enregistrer' : 'Ajouter'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showHistory && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowHistory(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '6px', width: '100%', maxWidth: '900px', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} data-testid="equipment-history-modal">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}><ClockCounterClockwise size={20} /> Historique des affectations materiel</h3>
              <button className="btn btn-secondary btn-sm btn-icon" onClick={() => setShowHistory(false)}><X size={16} /></button>
            </div>
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #E2E2E6' }}>
              <input type="text" className="form-input" placeholder="Rechercher par salarie ou materiel..." value={historyFilter} onChange={(e) => setHistoryFilter(e.target.value)} style={{ width: '100%' }} />
            </div>
            <div style={{ overflow: 'auto', flex: 1 }}>
              {history.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>Aucun historique pour le moment.</div>
              ) : (
                <table className="data-table" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Date remise</th>
                      <th>Materiel</th>
                      <th>Salarie</th>
                      <th>Etat</th>
                      <th>Date retour</th>
                      <th>Etat retour</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history
                      .filter(h => {
                        if (!historyFilter) return true;
                        const s = historyFilter.toLowerCase();
                        return (h.user_name || '').toLowerCase().includes(s) || (h.equipment_name || '').toLowerCase().includes(s);
                      })
                      .map(h => {
                        const dAssign = h.assigned_at ? fmtDateFR(h.assigned_at) : '-';
                        const dReturn = h.returned_at ? fmtDateFR(h.returned_at) : '-';
                        return (
                          <tr key={h.id}>
                            <td>{dAssign}</td>
                            <td style={{ fontWeight: '600' }}>{h.equipment_name}</td>
                            <td>{h.user_name}<div style={{ fontSize: '0.72rem', color: '#9E9E9E' }}>{h.user_job}</div></td>
                            <td>{h.condition_at_assignment || '-'}</td>
                            <td>{dReturn}</td>
                            <td>{h.return_condition || '-'}</td>
                            <td>
                              {h.is_open ? (
                                <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '600', backgroundColor: '#E3F2FD', color: '#1565C0' }}>En cours</span>
                              ) : (
                                <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '600', backgroundColor: '#E8F5E9', color: '#2E7D32' }}>Rendu</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
      {showReports && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowReports(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '6px', width: '100%', maxWidth: '1000px', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} data-testid="equipment-reports-modal">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#FFEBEE' }}>
              <h3 style={{ margin: 0, color: '#C62828', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Warning size={20} /> Signalements materiel
              </h3>
              <button className="btn btn-secondary btn-sm btn-icon" onClick={() => setShowReports(false)}><X size={16} /></button>
            </div>
            <div style={{ padding: '10px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', gap: '8px' }}>
              {[
                { k: 'open', label: 'Ouverts', count: reports.filter(r => r.status === 'ouvert').length },
                { k: 'resolved', label: 'Resolus', count: reports.filter(r => r.status === 'resolu').length },
                { k: 'all', label: 'Tous', count: reports.length },
              ].map(f => (
                <button key={f.k} onClick={() => setReportsFilter(f.k)}
                  className={`btn btn-sm ${reportsFilter === f.k ? 'btn-primary' : 'btn-secondary'}`}
                  data-testid={`reports-filter-${f.k}`}>
                  {f.label} ({f.count})
                </button>
              ))}
            </div>
            <div style={{ overflow: 'auto', flex: 1 }}>
              {(() => {
                const filtered = reports.filter(r => reportsFilter === 'all' || (reportsFilter === 'open' && r.status === 'ouvert') || (reportsFilter === 'resolved' && r.status === 'resolu'));
                if (filtered.length === 0) return <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>Aucun signalement</div>;
                return (
                  <table className="data-table" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Materiel</th>
                        <th>Salarie</th>
                        <th>Type</th>
                        <th>Description</th>
                        <th>Statut</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(r => {
                        const typeColors = { casse: '#C62828', vol: '#424242', reparation: '#EF6C00', autre: '#5E5F66' };
                        const typeLabels = { casse: 'Casse', vol: 'Vol', reparation: 'Reparation', autre: 'Autre' };
                        return (
                          <tr key={r.id} data-testid={`report-row-${r.id}`}>
                            <td>{r.reported_at ? fmtDateFR(r.reported_at) : '-'}</td>
                            <td style={{ fontWeight: '600' }}>{r.equipment_name}</td>
                            <td>{r.user_name}</td>
                            <td>
                              <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '600', backgroundColor: `${typeColors[r.issue_type]}15`, color: typeColors[r.issue_type] }}>
                                {typeLabels[r.issue_type] || r.issue_type}
                              </span>
                            </td>
                            <td style={{ fontSize: '0.82rem', color: '#5E5F66', maxWidth: '280px' }}>{r.description || '-'}</td>
                            <td>
                              {r.status === 'ouvert' ? (
                                <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '600', backgroundColor: '#FFEBEE', color: '#C62828' }}>Ouvert</span>
                              ) : (
                                <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '600', backgroundColor: '#E8F5E9', color: '#2E7D32' }}>Resolu</span>
                              )}
                            </td>
                            <td>
                              {r.status === 'ouvert' && (
                                <button className="btn btn-primary btn-sm" onClick={() => resolveReport(r.id)} data-testid={`resolve-${r.id}`}>
                                  <CheckCircle size={14} /> Resoudre
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentPage;
